// Normalise camera images before upload.
//
// iPhones produce HEIC. Google Cloud Vision cannot decode HEIC at all, so the
// OCR extension processed receipts, found nothing, and logged "no text found in
// image ...HEIC" as a success. Non-Safari browsers cannot render HEIC either, so
// a job photo taken on an iPhone is invisible to a Chrome or Android viewer - and
// Facebook and Google will reject it outright in Phase 4.
//
// Everything that leaves the device becomes a JPEG.

const JPEG = 'image/jpeg';

/** Re-extension a filename for JPEG output. Pure, so it is testable. */
export function jpegFileName(name = '') {
  const base = String(name).replace(/\.[^.\s]{1,5}$/i, '');
  return `${base || 'image'}.jpg`;
}

/** True for formats a browser may decode but Cloud Vision and the web cannot. */
export function needsConversion(file) {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  if (type === JPEG) return false;
  return (
    type.includes('heic') ||
    type.includes('heif') ||
    /\.(heic|heif)$/.test(name) ||
    type === '' // iOS sometimes reports an empty type for camera captures
  );
}

/**
 * Draw through a canvas and re-encode as JPEG, downscaling to fit.
 *
 * Always passes 'image/jpeg' to toBlob. The old code passed file.type, and for
 * an unsupported type the canvas silently falls back to PNG while the caller
 * goes on labelling the result image/heic - so the bytes, the content type and
 * the extension all disagreed.
 */
export function toJpeg(file, { maxWidth = 2000, maxHeight = 2000, quality = 0.9 } = {}) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      // JPEG has no alpha; without this, transparent pixels come out black.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Could not convert the image to JPEG.'));
            return;
          }
          resolve(
            new File([blob], jpegFileName(file.name), {
              type: JPEG,
              lastModified: Date.now(),
            })
          );
        },
        JPEG,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      // Chrome and Firefox cannot decode HEIC, so there is nothing to draw.
      // Fail loudly: uploading the original would silently break OCR again.
      reject(
        new Error(
          'This browser cannot read that image format (HEIC). ' +
            'Take the photo on your phone, or set the camera to "Most Compatible".'
        )
      );
    };

    img.src = url;
  });
}

/**
 * Prepare any camera image for upload. Converts when the format is unusable and
 * downscales oversized photos; otherwise returns the file untouched.
 */
export async function prepareImageForUpload(file, options = {}) {
  if (!file) return file;
  const oversized = file.size > 2 * 1024 * 1024;
  if (!needsConversion(file) && !oversized) return file;
  return toJpeg(file, options);
}
